import React, { useState } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ScrollView, Alert } from 'react-native';
import AppTextInput from './AppTextInput';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import { Appointment } from '../utils/appointmentManager';

interface Worker {
  id: string;
  name: string;
  phone: string;
  email: string;
  specialties: string;
}

interface CreateAppointmentModalProps {
  visible: boolean;
  onClose: () => void;
  workers: Worker[];
  onSave: (appointment: Omit<Appointment, 'id' | 'createdAt'>) => void;
}

const CreateAppointmentModal: React.FC<CreateAppointmentModalProps> = ({
  visible,
  onClose,
  workers,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    customerName: '',
    phone: '',
    email: '',
    address: '',
    serviceType: '',
    appointmentDate: new Date().toISOString().split('T')[0],
    appointmentTime: '10:00 AM',
    specialInstructions: '',
    workerId: '',
    estimatedCost: '',
  });

  const handleSave = () => {
    // Validate required fields
    if (!formData.customerName || !formData.phone || !formData.serviceType || !formData.appointmentDate || !formData.appointmentTime) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      Alert.alert('Error', 'Please enter a valid email address');
      return;
    }

    // Validate phone format (basic)
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!phoneRegex.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      Alert.alert('Error', 'Please enter a valid phone number');
      return;
    }

    const selectedWorker = workers.find(w => w.id === formData.workerId);
    
    const appointmentData: Omit<Appointment, 'id' | 'createdAt'> = {
      customerName: formData.customerName,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      serviceType: formData.serviceType,
      appointmentDate: formData.appointmentDate,
      appointmentTime: formData.appointmentTime,
      status: 'confirmed',
      specialInstructions: formData.specialInstructions,
      workerId: formData.workerId,
      workerName: selectedWorker?.name,
      estimatedCost: formData.estimatedCost ? parseFloat(formData.estimatedCost) : undefined,
    };

    onSave(appointmentData);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      customerName: '',
      phone: '',
      email: '',
      address: '',
      serviceType: '',
      appointmentDate: new Date().toISOString().split('T')[0],
      appointmentTime: '10:00 AM',
      specialInstructions: '',
      workerId: '',
      estimatedCost: '',
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Create New Appointment</Text>
          <TouchableOpacity style={styles.closeButton} onPress={handleClose}>
            <Text style={styles.closeButtonText}>✕</Text>
          </TouchableOpacity>
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.form}>
            {/* Customer Name */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Customer Name *</Text>
              <AppTextInput
                style={styles.input}
                value={formData.customerName}
                onChangeText={(text) => setFormData(prev => ({ ...prev, customerName: text }))}
                placeholder="Enter customer name"
              />
            </View>

            {/* Phone */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Phone Number *</Text>
              <AppTextInput
                style={styles.input}
                value={formData.phone}
                onChangeText={(text) => setFormData(prev => ({ ...prev, phone: text }))}
                placeholder="Enter phone number"
                keyboardType="phone-pad"
              />
            </View>

            {/* Email */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Email</Text>
              <AppTextInput
                style={styles.input}
                value={formData.email}
                onChangeText={(text) => setFormData(prev => ({ ...prev, email: text }))}
                placeholder="Enter email address"
                keyboardType="email-address"
                autoCapitalize="none"
              />
            </View>

            {/* Address */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Address</Text>
              <AppTextInput
                style={styles.input}
                value={formData.address}
                onChangeText={(text) => setFormData(prev => ({ ...prev, address: text }))}
                placeholder="Enter address"
                multiline
                numberOfLines={2}
              />
            </View>

            {/* Service Type */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Service Type *</Text>
              <AppTextInput
                style={styles.input}
                value={formData.serviceType}
                onChangeText={(text) => setFormData(prev => ({ ...prev, serviceType: text }))}
                placeholder="e.g., Haircut, Massage, Consultation"
              />
            </View>

            {/* Date */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Appointment Date *</Text>
              <DatePicker
                value={formData.appointmentDate}
                onChange={(date) => setFormData(prev => ({ ...prev, appointmentDate: date }))}
                placeholder="Select date"
              />
            </View>

            {/* Time */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Appointment Time *</Text>
              <TimePicker
                value={formData.appointmentTime}
                onChange={(time) => setFormData(prev => ({ ...prev, appointmentTime: time }))}
                placeholder="Select time"
              />
            </View>

            {/* Worker */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Assign Worker</Text>
              <View style={styles.workerContainer}>
                {workers.map((worker) => (
                  <TouchableOpacity
                    key={worker.id}
                    style={[
                      styles.workerOption,
                      formData.workerId === worker.id && styles.workerOptionSelected
                    ]}
                    onPress={() => setFormData(prev => ({ ...prev, workerId: worker.id }))}
                  >
                    <Text style={[
                      styles.workerOptionText,
                      formData.workerId === worker.id && styles.workerOptionTextSelected
                    ]}>
                      {worker.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Estimated Cost */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Estimated Cost ($)</Text>
              <AppTextInput
                style={styles.input}
                value={formData.estimatedCost}
                onChangeText={(text) => setFormData(prev => ({ ...prev, estimatedCost: text }))}
                placeholder="0.00"
                keyboardType="numeric"
              />
            </View>

            {/* Special Instructions */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Special Instructions</Text>
              <AppTextInput
                style={[styles.input, styles.textArea]}
                value={formData.specialInstructions}
                onChangeText={(text) => setFormData(prev => ({ ...prev, specialInstructions: text }))}
                placeholder="Any special notes or instructions"
                multiline
                numberOfLines={3}
              />
            </View>
          </View>
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity style={styles.cancelButton} onPress={handleClose}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.saveButton} onPress={handleSave}>
            <Text style={styles.saveButtonText}>Create Appointment</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: 'white',
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f8f9fa',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 16,
    color: '#6c757d',
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
  },
  form: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 12,
    fontSize: 16,
    backgroundColor: 'white',
    color: '#333',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  workerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  workerOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#ddd',
  },
  workerOptionSelected: {
    backgroundColor: '#007bff',
    borderColor: '#007bff',
  },
  workerOptionText: {
    fontSize: 14,
    color: '#6c757d',
  },
  workerOptionTextSelected: {
    color: 'white',
    fontWeight: '600',
  },
  footer: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#f8f9fa',
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#6c757d',
    fontWeight: '600',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#007bff',
    alignItems: 'center',
  },
  saveButtonText: {
    fontSize: 16,
    color: 'white',
    fontWeight: '600',
  },
});

export default CreateAppointmentModal;
